import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { PageHeader } from '../../components/common/PageHeader';
import { partnerApi, type ApiResponse } from '../../lib/api';
import { AiToolsTabs } from '../ai-tools/AiToolsTabs';
import styles from './AgentsPage.module.css';

const AGENTS_PAGE_SIZE = 25;

const AGENTS_COPY = {
  description: "Every tenant's AI agents and the tools they can use.",
  emptyNote: 'No agents yet. Agents are created when tenants activate AI features.',
  loadErrorNote: 'Your agents could not be loaded. Refresh the page to try again.',
  noToolsLabel: '—',
} as const;

const AGENT_TYPE_LABELS: Record<PartnerAgent['type'], string> = {
  voice: 'Voice',
  generic: 'Generic',
};

interface PartnerAgent {
  id: string;
  type: 'voice' | 'generic';
  tenantName: string;
  tenantExternalId: string;
  tools: string[];
}

interface AgentsListResult {
  agents: PartnerAgent[];
  pagination: { hasNextPage: boolean; cursor?: string | null };
}

const fetchAgentsPage = async (cursor?: string): Promise<AgentsListResult> => {
  const params = new URLSearchParams({ limit: String(AGENTS_PAGE_SIZE) });
  if (cursor) {
    params.set('cursor', cursor);
  }
  const response = await partnerApi.request<ApiResponse<AgentsListResult>>(`/partner/agents?${params}`);
  return response.data;
};

export const AgentsPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<Record<number, string | undefined>>({});

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['partner-agents', currentPage, pageCursors[currentPage]],
    queryFn: () => fetchAgentsPage(pageCursors[currentPage]),
  });

  // Store the next page's cursor when a page arrives, so Next can request it.
  useEffect(() => {
    if (data?.pagination.hasNextPage && data.pagination.cursor) {
      setPageCursors(prev => ({ ...prev, [currentPage + 1]: data.pagination.cursor ?? undefined }));
    }
  }, [data, currentPage]);

  const agents = data?.agents ?? [];
  const hasPreviousPage = currentPage > 1;
  const hasNextPage = data?.pagination.hasNextPage ?? false;
  const showPager = hasPreviousPage || hasNextPage;

  return (
    <div className={styles.page}>
      <AiToolsTabs active="agents" />
      <PageHeader title="Agents" description={AGENTS_COPY.description} />

      {isLoading ? (
        <p className={styles.loading}>Loading…</p>
      ) : isError ? (
        <p className={styles.error}>{AGENTS_COPY.loadErrorNote}</p>
      ) : (
        <>
          {agents.length === 0 ? (
            <p className={styles.empty}>{AGENTS_COPY.emptyNote}</p>
          ) : (
            <div className={styles.agentsTable}>
              <table>
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>External ID</th>
                    <th>Type</th>
                    <th>Tools</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map(agent => (
                    <AgentRow key={agent.id} agent={agent} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {showPager && (
            <footer className={styles.pager}>
              <button
                type="button"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={!hasPreviousPage || isFetching}>
                Previous
              </button>
              <span>Page {currentPage}</span>
              <button
                type="button"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={!hasNextPage || isFetching}>
                Next
              </button>
            </footer>
          )}
        </>
      )}
    </div>
  );
};

const AgentRow = ({ agent }: { agent: PartnerAgent }) => {
  const sortedTools = [...agent.tools].sort((a, b) => a.localeCompare(b));

  return (
    <tr>
      <td>{agent.tenantName}</td>
      <td className={styles.externalId}>{agent.tenantExternalId}</td>
      <td>{AGENT_TYPE_LABELS[agent.type]}</td>
      <td>
        {sortedTools.length > 0 ? (
          <div className={styles.toolPills}>
            {sortedTools.map(tool => (
              <span key={tool} className={styles.toolPill}>
                {tool}
              </span>
            ))}
          </div>
        ) : (
          <span className={styles.tools}>{AGENTS_COPY.noToolsLabel}</span>
        )}
      </td>
    </tr>
  );
};
